import dayjs from 'dayjs'
import {
	ConsumableType,
	DiscrepancyItem,
	MaterialFilters,
	MaterialRequest,
	PaginatedResponse,
	MaterialReturnFilters,
	SpecConsumableType,
	TaskMaterialDemands,
	TaskMaterialRequest,
	MaterialReturn,
	ReturnItem,
} from '../types/Material/Material'
import {
	deleteOne,
	getAll,
	getOne,
	patch,
	post,
	postWithResponse,
} from './CommonServices/httpService'
import { RequestWorker } from '../types/Work/Request'
import { showErrorToast, showSuccessToast } from './CommonServices/alertServices'
import { WorkItem } from '../types/Work/WorkItem'
import { Order, Pallet } from '../types/Material/Order'
import { AttachmentsUploadResponse } from '../types/Upload/AttachmentResponse'
import { uploadImages } from './CommonServices/imageAttachmentService'
import { getForemanStagedPalletsFB } from './CacheFallbackServices/materialFallback'
import { ForemanDB } from '../types/IndexedDB/ForemanDB'
import { mapPallets } from './CacheMapperServices/materialMapper'
import { hasConnection } from './CommonServices/healthGate'

export const getMaterialRequests = async (filters: MaterialFilters) => {
	const queryParams = new URLSearchParams()

	if (filters.page) queryParams.append('page', filters.page.toString())
	if (filters.status) queryParams.append('status', filters.status)
	if (filters.assignedToId) queryParams.append('assignedToId', filters.assignedToId.toString())
	if (filters.searchWorkItem) queryParams.append('searchWorkItem', filters.searchWorkItem)
	if (filters.kittedOn) queryParams.append('kittedOn', dayjs(filters.kittedOn).utc().toISOString())
	if (filters.stagedOn) queryParams.append('stagedOn', dayjs(filters.stagedOn).utc().toISOString())
	if (filters.requestedOn)
		queryParams.append('requestedOn', dayjs(filters.requestedOn).utc().toISOString())
	if (filters.matReqId) queryParams.append('matReqId', filters.matReqId.toString())
	if (filters.receivedOn)
		queryParams.append('receivedOn', dayjs(filters.receivedOn).utc().toISOString())
	if (filters.receivedById) queryParams.append('receivedById', filters.receivedById.toString())

	const queryString = queryParams.toString()
	const url = `/Materials${queryString ? `?${queryString}` : ''}`
	const response = await getAll<PaginatedResponse<MaterialRequest>>(url)
	return response.data
}

export const getMaterialAssignees = async () => {
	const response = await getAll<RequestWorker[]>('/Materials/Assignees')
	return response.data
}

export const getAllMaterialReceivers = async () => {
	const response = await getAll<RequestWorker[]>('/Materials/Receivers/All')
	return response.data
}

export const assignMaterialRequests = async (assigneeId: number, materialRequestIds: number[]) => {
	const response = await patch(`/Materials/Assign/${assigneeId}`, materialRequestIds)
}

export const createMaterialRequest = async (
	workItemIds: number[],
	priority: number,
	callback: (errorMessage: string) => void,
	skipSurvey?: boolean,
) => {
	try {
		const response = await post(
			`/Materials?priority=${priority}${skipSurvey ? '&skipSurvey=true' : ''}`,
			[...workItemIds],
		)
		if (response.success) {
			showSuccessToast('Material requests created successfully', true)
		}
	} catch (error: unknown) {
		if (typeof error === 'object' && error && 'eventId' in error && error.eventId === 302501) {
			// Narrow the type to include message property
			const errorWithMessage = error as { eventId: number; message: string }
			callback(errorWithMessage.message)
		} else {
			showErrorToast({
				message: 'Failed to create material requests',
			})
		}
	}
}

export const createInventoryMaterialRequest = async (
	taskId: number,
	requestedItems: Record<number, number>,
) => {
	const response = await post(`/Materials/Task/${taskId}`, requestedItems)
	return response.success
}

export const getTaskMaterialRequests = async (taskId: number) => {
	const response = await getAll<TaskMaterialRequest[]>(`/Materials/Task/${taskId}/Requests`)
	return response.data
}

export const getTaskMaterialDemands = async (taskId: number) => {
	const response = await getAll<TaskMaterialDemands[]>(`/Materials/Task/${taskId}/Demands`)
	return response.data
}

export const getMaterialWorkItems = async (materialRequestId: number) => {
	const response = await getOne<WorkItem>(`/Materials/${materialRequestId}/WorkItems`)
	return response.data
}

export const getMaterialOrders = async (materialRequestId: number) => {
	const response = await getAll<Order[]>(`/Materials/${materialRequestId}/Orders`)
	return response.data
}

export const createNewOrder = async (materialRequestId: number) => {
	const response = await post(`/Materials/${materialRequestId}/Order`, {})
}

export const addPalletToOrder = async (orderId: number) => {
	const response = await post(`/Materials/${orderId}/Pallet`, {})
}

export const stagePallet = async (palletId: number) => {
	const response = await patch(`/Materials/Pallet/${palletId}/Stage`, {})
}

export const addComponentToPallet = async (
	componentId: number,
	palletId: number,
	quantity: number,
	taskId: number,
) => {
	const response = await post(
		`/Materials/Component/${componentId}/Pallet/${palletId}?quantity=${quantity}&taskId=${taskId}`,
		{},
	)
}

export const savePalletComment = async (palletId: number, comment: string) => {
	const response = await post(`/Materials/Pallet/${palletId}/Comment`, { comment })
}

export const updatePalletComponentQuantity = async (palletComponentId: number, newQty: number) => {
	const response = await patch(`/Materials/PalletComponent/${palletComponentId}`, {
		newQty,
	})
}
export const deletePalletComponent = async (palletComponentId: number) => {
	const response = await deleteOne(`/Materials/PalletComponent/${palletComponentId}`)
}

export const deletePallet = async (palletId: number) => {
	const response = await deleteOne(`/Materials/Pallet/${palletId}`)
}

export const getPalletAttachments = async (palletId: number) => {
	const response = await getAll<AttachmentsUploadResponse[]>(
		`/Materials/Pallet/${palletId}/Attachments`,
	)
	return response.data
}

export const addPalletAttachments = async (palletId: number, attachments: Blob[]) => {
	const response = await postWithResponse<AttachmentsUploadResponse[]>(
		`/Materials/Pallet/${palletId}/Attachments?attachmentsCount=${attachments.length}`,
		{},
	)
	if (response.data && attachments.length > 0) {
		await uploadImages(response.data, attachments)
	}
	return response.data
}

export const deletePalletAttachment = async (attachmentId: number) => {
	await deleteOne(`/Materials/PalletAttachment/${attachmentId}`)
}

export const getForemanStagedPallets = async (foremanId: number, db: ForemanDB | null) => {
	if (!(await hasConnection()) && db) {
		try {
			return await getForemanStagedPalletsFB(db)
		} catch (error: any) {
			showErrorToast({ message: error.message || 'Failed to get staged pallets from cache' })
			throw error
		}
	}
	const response = await getAll<Pallet[]>(`/Materials/Foreman/${foremanId}/Staged`)
	if (db) {
		mapPallets(db, response.data)
	}
	return response.data
}

export const getForemanReceivedPallets = async (foremanId: number) => {
	if (!(await hasConnection())) {
		return []
	}
	const response = await getAll<Pallet[]>(`/Materials/Foreman/${foremanId}/Received`)
	return response.data
}

export const getForemanOrderSummary = async (foremanId: number) => {
	if (!(await hasConnection())) {
		return { remainingOrders: 0, remainingPallets: 0 }
	}
	const response = await getOne<{ remainingOrders: number; remainingPallets: number }>(
		`/Materials/Foreman/${foremanId}/Summary`,
	)
	return response.data
}

export const receivePallet = async (palletId: number) => {
	const response = await patch(`/Materials/Pallet/${palletId}/Receive`, {})
}

export const setWorkItemKitted = async (workItemId: number, requestId: number) => {
	const response = await patch(`/Materials/${requestId}/WorkItem/${workItemId}`, {})
}

export const getMissingWorkItems = async (requestId: number) => {
	const response = await getOne<{
		workItemType: string
		splittedRequests: number
		workItemName: string
	} | null>(`/Materials/${requestId}/Missing`)
	return response.data
}

export const getProjectConsumables = async () => {
	const response = await getAll<SpecConsumableType[]>(`/Materials/ConsumableType`)
	return response.data
}

export const createConsumableType = async (consumableTypes: ConsumableType[], specId: number) => {
	const response = await post(
		`/Materials/ConsumableType`,
		consumableTypes.map((consumable) => ({ ...consumable, specId })),
	)
}

export const deleteConsumableType = async (consumableId: number) => {
	const response = await deleteOne(`/Materials/ConsumableType/${consumableId}`)
}

export const addConsumableToPallet = async (
	consumableId: number,
	palletId: number,
	quantity: number,
	taskId: number,
) => {
	const response = await post(
		`/Materials/Consumable/${consumableId}/Pallet/${palletId}?quantity=${quantity}&taskId=${taskId}`,
		{},
	)
}

export const updatePalletConsumableQuantity = async (
	palletConsumableId: number,
	newQty: number,
) => {
	const response = await patch(`/Materials/PalletConsumable/${palletConsumableId}`, { newQty })
}

export const deletePalletConsumable = async (palletConsumableId: number) => {
	const response = await deleteOne(`/Materials/PalletConsumable/${palletConsumableId}`)
}

export const addInventoryItemToPallet = async (
	inventoryItemId: number,
	palletId: number,
	quantity: number,
	taskId: number,
) => {
	const response = await post(
		`/Materials/InventoryItem/${inventoryItemId}/Pallet/${palletId}?quantity=${quantity}&taskId=${taskId}`,
		{},
	)
}

export const updatePalletInventoryItemQuantity = async (
	palletInventoryItemId: number,
	newQty: number,
) => {
	const response = await patch(`/Materials/PalletInventoryItem/${palletInventoryItemId}`, {
		newQty,
	})
}

export const deletePalletInventoryItem = async (palletInventoryItemId: number) => {
	const response = await deleteOne(`/Materials/PalletInventoryItem/${palletInventoryItemId}`)
}

export const createDiscrepancy = async (
	palletId: number,
	foremanId: number,
	discrepancies: DiscrepancyItem[],
	reason: number,
	comment: string,
) => {
	const response = await post(`/Materials/Discrepancy`, {
		discrepancyItems: discrepancies,
		palletId,
		foremanId,
		reason,
		comment,
	})
}

export const getMaterialReturns = async (filters: MaterialReturnFilters) => {
	const queryParams = new URLSearchParams()

	if (filters.page) queryParams.append('page', filters.page.toString())
	if (filters.status) queryParams.append('status', filters.status)
	if (filters.requestedById) queryParams.append('requestedById', filters.requestedById.toString())
	if (filters.searchWorkItem) queryParams.append('searchWorkItem', filters.searchWorkItem)
	if (filters.approvedOn)
		queryParams.append('approvedOn', dayjs(filters.approvedOn).utc().toISOString())
	if (filters.requestedOn)
		queryParams.append('requestedOn', dayjs(filters.requestedOn).utc().toISOString())
	if (filters.matRetId) queryParams.append('matRetId', filters.matRetId.toString())
	const queryString = queryParams.toString()
	const url = `/Materials/Returns${queryString ? `?${queryString}` : ''}`
	const response = await getAll<PaginatedResponse<MaterialReturn>>(url)

	return response.data
}

export const getMaterialReturnRequesters = async () => {
	const response = await getAll<RequestWorker[]>('/Materials/Returns/Requesters')
	return response.data
}

export const getReturnItems = async (materialReturnId: number) => {
	const response = await getAll<ReturnItem[]>(`/Materials/Returns/${materialReturnId}`)
	return response.data
}

export const approveMaterialReturn = async (materialReturnId: number, returnItems: any[]) => {
	const response = await patch(`/Materials/Returns/${materialReturnId}/Approve`, returnItems)
	return response.success
}

export const getWorkerPendingPalletsCount = async (assigneeId: number) => {
	const response = await getOne<number>(`/Materials/Worker/${assigneeId}/PendingPallets`)
	return response.data
}